import React from "react";
import { Box, Typography, Grid, Container, useTheme } from "@mui/material";
import { styled } from "@mui/system";
import { motion } from "framer-motion";

// Final images
import iconCafe from "../../components/assets/whoHosts/Cafe.png";
import iconSchool from "../../components/assets/whoHosts/schools.png";
import iconOffice from "../../components/assets/whoHosts/offices.png";
import iconHotel from "../../components/assets/whoHosts/hotel.png";

// MUI Icons
import {
  EmojiEmotions,
  Brush,
  RecordVoiceOver,
  Celebration,
} from "@mui/icons-material";

/* ——— Netflix-ish tokens ——— */
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  text: "#ffffff",
  textMuted: "rgba(255,255,255,0.74)",
  border: "rgba(255,255,255,0.12)",
  red: "#E50914",
  redHover: "#b20710",
};
const RADIUS = 18;

const SectionRoot = styled(Box)(({ theme }) => ({
  width: "100%",
  position: "relative",
  background: COLORS.bg,
  fontFamily: "Lexend, sans-serif",
  padding: theme.spacing(10, 2),
  overflow: "hidden",
  // vignette and soft glow accents
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background:
      "radial-gradient(900px 420px at 10% 10%, rgba(229,9,20,0.14), transparent 60%), radial-gradient(800px 420px at 85% 10%, rgba(255,255,255,0.06), transparent 60%)",
    pointerEvents: "none",
  },
}));

const MotionBox = motion(Box);

/* Venue tile */
const ImageWrapper = styled(motion.div)(({ theme }) => ({
  width: "100%",
  maxWidth: 220,
  aspectRatio: "11/8",
  margin: "0 auto",
  borderRadius: RADIUS,
  overflow: "hidden",
  background: COLORS.panel,
  border: `1px solid ${COLORS.border}`,
  boxShadow:
    "0 16px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04) inset",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const ItemImage = styled("img")(() => ({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
  transition: "transform .28s ease",
  willChange: "transform",
}));

/* Bullet card */
const BulletCard = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.75),
  padding: theme.spacing(2.25),
  borderRadius: RADIUS,
  backgroundColor: COLORS.panel,
  color: COLORS.text,
  border: `1px solid ${COLORS.border}`,
  boxShadow:
    "0 16px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04) inset",
  transition: "border-color 180ms ease, transform 220ms ease",
  "&:hover": {
    borderColor: COLORS.red,
    transform: "translateY(-2px)",
  },
}));

const WhoHostsSection: React.FC = () => {
  const theme = useTheme();

  const venues = [
    { icon: iconCafe, title: "Cafés", subtitle: "& Lounges" },
    { icon: iconSchool, title: "Schools", subtitle: "& Classes" },
    { icon: iconOffice, title: "Offices", subtitle: "& Workshops" },
    { icon: iconHotel, title: "Hotels", subtitle: "& Events" },
  ];

  const bullets = [
    {
      icon: <EmojiEmotions sx={{ fontSize: 30, color: COLORS.text }} />,
      text: "Live music in your café every Friday night",
    },
    {
      icon: <Brush sx={{ fontSize: 30, color: COLORS.text }} />,
      text: "Art sessions broadcast to classrooms",
    },
    {
      icon: <RecordVoiceOver sx={{ fontSize: 30, color: COLORS.text }} />,
      text: "Invite speakers to corporate events",
    },
    {
      icon: <Celebration sx={{ fontSize: 30, color: COLORS.text }} />,
      text: "Host themed events in hotel lounges",
    },
  ];

  return (
    <SectionRoot>
      <Container
        maxWidth="lg"
        sx={{
          px: { xs: 2.5, md: 6 },
          [theme.breakpoints.up("lg")]: { px: 8 },
        }}
      >
        <MotionBox
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          {/* Heading */}
          <Typography
            variant="h4"
            fontWeight={900}
            mb={1}
            sx={{ color: COLORS.text, letterSpacing: "-0.02em" }}
          >
            WHO HOSTS
          </Typography>

          <Typography
            variant="h6"
            fontWeight={800}
            sx={{ color: COLORS.textMuted }}
            mb={3}
          >
            Bring the Stage to <em>Your</em> Space
          </Typography>

          <Typography
            variant="body1"
            sx={{ color: COLORS.textMuted, maxWidth: 900, mb: 6 }}
          >
            StageLink™ empowers venues like cafés, lounges, classrooms, and
            corporate offices to host immersive live talent experiences. Whether
            it's a musical performance, an art class, or a speaker session —
            your space becomes a stage for real-time connection.
          </Typography>

          {/* Venues */}
          <Grid container spacing={4} justifyContent="center" mb={6}>
            {venues.map((item, idx) => (
              <Grid item xs={6} sm={3} key={idx} textAlign="center">
                <ImageWrapper
                  initial={{ opacity: 0, scale: 0.96 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.35, delay: idx * 0.08 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.02 }}
                >
                  <ItemImage src={item.icon} alt={item.title} />
                </ImageWrapper>
                <Typography
                  variant="subtitle1"
                  fontWeight={800}
                  mt={1.25}
                  sx={{ color: COLORS.text }}
                >
                  {item.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: COLORS.textMuted, fontWeight: 600 }}
                >
                  {item.subtitle}
                </Typography>
              </Grid>
            ))}
          </Grid>

          {/* Bullet cards (2x2 on mobile, 4 across on md+) */}
          <Grid container spacing={2.25} justifyContent="center" sx={{ mt: 2 }}>
            {bullets.map((item, idx) => (
              <Grid item xs={6} sm={6} md={3} key={idx}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: idx * 0.08 }}
                  style={{ height: "100%" }}
                >
                  <BulletCard
                    sx={{
                      height: "100%",
                      textAlign: "center",
                      p: { xs: 2, sm: 2.25 },
                      minHeight: { xs: 120, sm: 140 },
                    }}
                  >
                    <Box sx={{ mb: { xs: 1, sm: 1.25 } }}>{item.icon}</Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: COLORS.textMuted,
                        fontWeight: 600,
                        fontSize: { xs: "0.85rem", md: "0.95rem" },
                        lineHeight: 1.4,
                      }}
                    >
                      {item.text}
                    </Typography>
                  </BulletCard>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* CTA */}
          <Typography
            variant="h6"
            fontWeight={800}
            mt={8}
            textAlign="center"
            sx={{
              color: COLORS.text,
              fontSize: { xs: "1.125rem", sm: "1.25rem" },
              px: { xs: 2, sm: 0 },
            }}
          >
            ✨ You bring the space. We bring the talent. Let’s create
            unforgettable experiences — on-screen and on-site.
          </Typography>
        </MotionBox>
      </Container>
    </SectionRoot>
  );
};

export default WhoHostsSection;
